import React, { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { URLS } from '@wix/da-url';
import MessageModal from '@wix/da-ds/pkg/modals/dsPresets/MessageModal';
import {
  CancelButton,
  ConfirmButton,
} from '@wix/da-ds/pkg/modals/dsPresets/MessageModal/buttons';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import BoostIllustration from '../BoostIllustration';
import { makeXHRCall } from '../../../../../simpleXHR/actionCreators';
import {
  SimpleXHREndpoint,
  SimpleXHRMethod,
} from '../../../../../simpleXHR/constants';
import { ResourceType } from '../../../../../types/resource';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { addErrorNotification } from '../../../../../Notifications/redux/actionCreators';
import { putEntities } from '../../../../../redux/entities/actions';
import { normalizeDeviation } from '../../../../../redux/normalizr/helpers';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import {
  BadgeAwardClickBiEvent,
  BiContextProvidedValue,
  BiData,
  BuyCoreButtonBiEvent,
  GetMorePointsBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';

import s from './BoostModal.scss';

interface Props {
  deviation: PapiDeviation;
  onSuccess: () => void;
  onRequestClose: () => void;
}

const BoostModal: React.FC<Props> = ({
  deviation,
  onSuccess,
  onRequestClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useContext(MobileContext);
  const { coreLevel } = useContext(SessionContext);
  const [isBusy, setIsBusy] = useState(false);
  const logBiEvent = useBiLogger();
  const highestCoreLevel = 'core_max';

  const {
    canUseFreeBoost,
    freeBoostsRemaining = 1,
    freeBoostsAllowed = 3,
    boostPointsPrice = 0,
    pointsBalance = 0,
  } = deviation.extended?.boostRestrictions || {};

  const remainingBoostsString =
    freeBoostsAllowed > 1
      ? `${freeBoostsRemaining} of ${freeBoostsAllowed}`
      : freeBoostsRemaining;

  const handleConfirm = () => {
    setIsBusy(true);
    if (!canUseFreeBoost && pointsBalance < boostPointsPrice) {
      logBiEvent(
        BiData<GetMorePointsBiEvent>({
          evid: 504,
          typeid: deviation.typeId,
          widgetname: 'boost_modal',
        })
      );
      window.location.href = URLS.POINTS;
      return;
    }
    logBiEvent(
      BiData<BadgeAwardClickBiEvent>({
        evid: 321,
        badge_type: 'boost',
        badge_typeid: 176,
        sectionname: BiContextProvidedValue,
        itemid: deviation.deviationId,
        typeid: deviation.typeId,
      })
    );
    dispatch(
      makeXHRCall({
        endpoint: SimpleXHREndpoint.GIVE_CORE_BADGE,
        method: SimpleXHRMethod.POST,
        data: {
          typeid: ResourceType.DEVIATION,
          itemid: deviation.deviationId,
          userid: deviation.author?.userId,
          badgetype: 'Boost',
          use_points: !canUseFreeBoost,
        },
        onSuccess: () => {
          const entities = normalizeDeviation({
            ...deviation,
            boosted: true,
            extended: {
              ...deviation.extended,
              boosted: true,
            },
          });
          dispatch(
            putEntities({
              entities,
            })
          );
          onSuccess();
        },
        onError: () => {
          dispatch(addErrorNotification(t('common.somethingWentWrong')));
        },
      })
    );
  };

  const renderUpgradeCoreLink = () => {
    if (coreLevel === highestCoreLevel) {
      return null;
    }
    return (
      <BiLink
        biData={BiData<BuyCoreButtonBiEvent>({
          evid: 375,
          referral_info: ReferralType.BOOST_DEVIATION_POPUP,
        })}
        href={URLS.CORE_MEMBERSHIP}
        className="link"
      >
        {t('boostDeviation.modal.upgradeYourPlan')}
      </BiLink>
    );
  };

  const renderMessage = () => (
    <div className={s['message']}>
      <div>
        <Trans i18nKey="boostDeviation.modal.message">
          Your deviation will be more visible in browse for the next 48 hours.{' '}
          <a
            className="link"
            target="_blank"
            href={t('boostDeviation.learnMoreUrl')}
            rel="noreferrer"
          >
            Learn more
          </a>
        </Trans>
      </div>
      <br />
      {canUseFreeBoost ? (
        <>
          <div>
            <Trans
              i18nKey="boostDeviation.modal.remainingFreeBoosts"
              values={{ remainingBoostsString }}
              count={freeBoostsAllowed}
            >
              You have <strong>{remainingBoostsString}</strong> free boost this
              month.
            </Trans>
            {renderUpgradeCoreLink()}
          </div>
        </>
      ) : (
        <>
          <div>
            <Trans i18nKey="boostDeviation.modal.noFreeBoost">
              You have <strong>no remaining free boosts</strong> this
              month.{' '}
            </Trans>
            {renderUpgradeCoreLink()}
          </div>
          <div>
            <Trans
              i18nKey="boostDeviation.modal.pointsBalance"
              count={pointsBalance}
            >
              Balance: {{ pointsBalance }} Points.{' '}
              <BiLink
                biData={BiData<GetMorePointsBiEvent>({
                  evid: 504,
                  typeid: deviation.typeId,
                  widgetname: 'boost_modal',
                })}
                href={URLS.POINTS}
                className="link"
              >
                Get more
              </BiLink>
            </Trans>
          </div>
        </>
      )}
    </div>
  );

  if (!deviation.extended) {
    return <LoadingIndicator view="llama" usePortal withOverlay />;
  }

  return (
    <MessageModal
      isOpen
      onRequestClose={onRequestClose}
      image={<BoostIllustration />}
      variant={isMobile ? 'vertical' : 'horizontal'}
      contentClassName={s['content']}
      title={t('boostDeviation.modal.title')}
      message={renderMessage()}
      footerButtons={[
        <CancelButton key="cancel" onClick={onRequestClose}>
          {t('popup.cancel')}
        </CancelButton>,
        <ConfirmButton loading={isBusy} key="confirm" onClick={handleConfirm}>
          {canUseFreeBoost
            ? t('boostDeviation.modal.button.boostNow')
            : t('boostDeviation.modal.button.boostForPoints', {
                boostPointsPrice,
              })}
        </ConfirmButton>,
      ]}
    />
  );
};
BoostModal.displayName = 'BoostModal';

export default BoostModal;
